import { useCallback, useEffect, useRef, useState } from "react";

import {
  fetchIncidentSurface,
  type IncidentSurfacePayload,
  type ObservabilityScope,
  type ObservabilityService,
} from "./observability-api";

const DEFAULT_POLL_INTERVAL_MS = 30_000;

export interface UseIncidentSurfaceReturn {
  data: IncidentSurfacePayload | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useIncidentSurface(
  windowMinutes: number,
  scope: ObservabilityScope = "identity",
  service?: ObservabilityService,
  pollIntervalMs: number = DEFAULT_POLL_INTERVAL_MS
): UseIncidentSurfaceReturn {
  const [data, setData] = useState<IncidentSurfacePayload | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const requestIdRef = useRef(0);

  const load = useCallback(
    async (minutes: number) => {
      const requestId = ++requestIdRef.current;

      try {
        const payload = await fetchIncidentSurface(minutes, service, scope);
        if (requestId !== requestIdRef.current) return;
        setData(payload);
        setError(null);
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        setError(error instanceof Error ? error.message : "Failed to load incident surface");
      }

      if (requestId !== requestIdRef.current) return;
      setIsLoading(false);
    },
    [scope, service]
  );

  const startPolling = useCallback(
    (minutes: number) => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        void load(minutes);
      }, pollIntervalMs);
    },
    [load, pollIntervalMs]
  );

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const refresh = useCallback(() => {
    setIsLoading(true);
    setError(null);
    void load(windowMinutes);
  }, [load, windowMinutes]);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    void load(windowMinutes);
    startPolling(windowMinutes);

    return () => {
      requestIdRef.current += 1;
      stopPolling();
    };
  }, [load, startPolling, stopPolling, windowMinutes]);

  return {
    data,
    isLoading,
    error,
    refresh,
  };
}
